import React, { useState, useEffect } from 'react';
import { Line } from '../types';
import Modal from './Modal';
import InfeedSettingsV2 from './InfeedSettingsV2';
import OutfeedSettings from './OutfeedSettings';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { usePreferences } from '../contexts/PreferencesContext';

interface InputFieldProps {
  label: string;
  name: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  error?: string;
  required?: boolean;
  type?: string;
  min?: string;
  max?: string;
  step?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  lockedMessage?: string;
  unit?: string;
  defaultValue?: string | number;
  showDefaultIndicator?: boolean;
}

const InputField: React.FC<InputFieldProps> = ({ 
  label, name, value, onChange, error, required, type = 'text', min, max, step, children, disabled, lockedMessage, unit, defaultValue, showDefaultIndicator = false 
}) => {
  // Check if value differs from default
  const isDifferentFromDefault = showDefaultIndicator && defaultValue !== undefined && value !== defaultValue && value !== '';
  
  return (
    <div className="mb-4">
      <label htmlFor={name} className="block text-sm font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
        {disabled && <span className="ml-2 text-amber-600">🔒</span>}
        {isDifferentFromDefault && (
          <span className="ml-2 relative group">
            <span className="text-blue-600 font-bold cursor-help">•</span>
            <div className="absolute z-50 invisible group-hover:visible bg-slate-800 text-white text-xs rounded-md py-1 px-2 bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap shadow-lg">
              <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-800"></div>
              Default: {defaultValue}{unit ? ` ${unit}` : ''}
            </div>
          </span>
        )}
      </label>
    {type === 'select' ? (
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
          error ? 'border-red-500' : disabled ? 'border-slate-200 bg-slate-50' : 'border-slate-300'
        }`}
        aria-required={required}
        aria-describedby={error ? `${name}-error` : undefined}
      >
        {children}
      </select>
    ) : (
      <div className="relative">
        <input
          type={type}
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
            error ? 'border-red-500' : disabled ? 'border-slate-200 bg-slate-50' : 'border-slate-300'
          } ${unit ? 'pr-12' : ''}`}
          aria-required={required}
          aria-describedby={error ? `${name}-error` : undefined}
        />
        {unit && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
            {unit}
          </span>
        )}
      </div>
    )}
    {error && <p id={`${name}-error`} className="mt-1 text-xs text-red-600">{error}</p>}
    {lockedMessage && disabled && <p className="mt-1 text-xs text-amber-600">{lockedMessage}</p>}
  </div>
  );
};

interface ArmTimingData {
  stationNumber: number;
  armOpenDelay: number | '';
  armOpenDuration: number | '';
}

interface LineFormData {
  hardwareId: number | '';
  name: string;
  systemId: string;
  numberOfStations: number | '';
  numberOfProductPockets: number | '';
  // Infeed settings
  conveyorStartDelay: number | '';
  conveyorStopDelay: number | '';
  bufferHopperOpenDelay: number | '';
  bufferHopperCloseDelay: number | '';
  weighingDelay: number | '';
  zeroingDelay: number | '';
  zeroThresholdWeight: number | '';
  hopperCapacity: number | '';
  defaultInfeedTargetWeight: number | '';
  // Arm timing settings
  armTimingSettings: ArmTimingData[];
  // Outfeed settings
  outfeedHopperCapacity: number | '';
  outfeedCycleWaitTime: number | '';
  outfeedWeighDelay: number | '';
  outfeedZeroDelay: number | '';
  outfeedZeroThresholdWeight: number | '';
  outfeedBreakoutAttempts: number | '';
  outfeedPocketBufferHopperCloseDelay: number | '';
  pocketCloseDelays: (number | '')[];
}

interface LineFormModalV2Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (lineData: Omit<Line, 'id'> | Line, isNew: boolean) => void;
  initialData?: Line | null;
  title: string;
}

type TabType = 'basic' | 'infeed' | 'outfeed';
type OutfeedSubTabType = 'system' | 'pockets';

const LineFormModalV2: React.FC<LineFormModalV2Props> = ({ 
  isOpen, onClose, onSubmit, initialData, title 
}) => {
  const { apiUrl } = useDatabaseContext();
  const { weightUnit } = usePreferences();
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [outfeedSubTab, setOutfeedSubTab] = useState<OutfeedSubTabType>('system');
  const [systemDefaults, setSystemDefaults] = useState({
    conveyorStartDelay: 1000,
    conveyorStopDelay: 2000,
    bufferHopperOpenDelay: 500,
    bufferHopperCloseDelay: 1500,
    weighingDelay: 3000,
    zeroingDelay: 1000,
    zeroThresholdWeight: 0.1,
    hopperCapacity: 50.0,
    defaultInfeedTargetWeight: 10.0,
    // Arm timing defaults
    armTimingSettings: [] as ArmTimingData[],
    // Outfeed defaults
    outfeedHopperCapacity: 50.0,
    outfeedCycleWaitTime: 2000,
    outfeedWeighDelay: 3000,
    outfeedZeroDelay: 1000,
    outfeedZeroThresholdWeight: 0.1,
    outfeedBreakoutAttempts: 3,
    outfeedPocketBufferHopperCloseDelay: 1500,
    defaultPocketCloseDelays: [1500, 1500, 1500, 1500],
    // System defaults
    defaultNumberOfStations: 10,
    defaultNumberOfPockets: 3,
    lockNumberOfStations: false,
    lockNumberOfPockets: false
  });

  const [formData, setFormData] = useState<LineFormData>({
    hardwareId: '',
    name: '',
    systemId: '',
    numberOfStations: '',
    numberOfProductPockets: '',
    conveyorStartDelay: 1000,
    conveyorStopDelay: 2000,
    bufferHopperOpenDelay: 500,
    bufferHopperCloseDelay: 1500,
    weighingDelay: 3000,
    zeroingDelay: 1000,
    zeroThresholdWeight: 0.1,
    hopperCapacity: 50.0,
    defaultInfeedTargetWeight: 10.0,
    armTimingSettings: [],
    outfeedHopperCapacity: 50.0,
    outfeedCycleWaitTime: 2000,
    outfeedWeighDelay: 3000,
    outfeedZeroDelay: 1000,
    outfeedZeroThresholdWeight: 0.1,
    outfeedBreakoutAttempts: 3,
    outfeedPocketBufferHopperCloseDelay: 1500,
    pocketCloseDelays: []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [existingHardwareIds, setExistingHardwareIds] = useState<Array<{id: string, hardwareId: number, name: string}>>([]);
  const [availableSystems, setAvailableSystems] = useState<Array<{id: string, systemName: string}>>([]);

  // Fetch system defaults and existing hardware IDs when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchDefaults = async () => {
        try {
          const response = await fetch(`${apiUrl}/system-defaults`);
          if (response.ok) {
            const data = await response.json();
            const defaults = data.data;
            const armTimingSettings = defaults.armTimingSettings ? 
              (typeof defaults.armTimingSettings === 'string' ? 
                JSON.parse(defaults.armTimingSettings) : 
                defaults.armTimingSettings) : [];
                
            setSystemDefaults({
              conveyorStartDelay: defaults.conveyorStartDelay,
              conveyorStopDelay: defaults.conveyorStopDelay,
              bufferHopperOpenDelay: defaults.bufferHopperOpenDelay,
              bufferHopperCloseDelay: defaults.bufferHopperCloseDelay,
              weighingDelay: defaults.weighingDelay,
              zeroingDelay: defaults.zeroingDelay,
              zeroThresholdWeight: defaults.zeroThresholdWeight,
              hopperCapacity: defaults.hopperCapacity,
              defaultInfeedTargetWeight: defaults.defaultInfeedTargetWeight,
              armTimingSettings: armTimingSettings,
              outfeedHopperCapacity: defaults.outfeedHopperCapacity || 50.0,
              outfeedCycleWaitTime: defaults.outfeedCycleWaitTime || 2000,
              outfeedWeighDelay: defaults.outfeedWeighDelay || 3000,
              outfeedZeroDelay: defaults.outfeedZeroDelay || 1000,
              outfeedZeroThresholdWeight: defaults.outfeedZeroThresholdWeight || 0.1,
              outfeedBreakoutAttempts: defaults.outfeedBreakoutAttempts || 3,
              outfeedPocketBufferHopperCloseDelay: defaults.outfeedPocketBufferHopperCloseDelay || 1500,
              defaultPocketCloseDelays: defaults.defaultPocketCloseDelays ? 
                JSON.parse(defaults.defaultPocketCloseDelays) : [1500, 1500, 1500, 1500],
              defaultNumberOfStations: defaults.defaultNumberOfStations || 10,
              defaultNumberOfPockets: defaults.defaultNumberOfPockets || 3,
              lockNumberOfStations: defaults.lockNumberOfStations ?? false,
              lockNumberOfPockets: defaults.lockNumberOfPockets ?? false
            });
          }
        } catch (error) {
          console.error('Error fetching system defaults:', error);
        }
      };
      
      const fetchExistingHardwareIds = async () => {
        try {
          const response = await fetch(`${apiUrl}/lines`);
          if (response.ok) {
            const data = await response.json();
            const lines = data.data || [];
            const hardwareIds = lines
              .filter((line: any) => line.hardwareId !== null && line.hardwareId !== undefined)
              .map((line: any) => ({
                id: line.id,
                hardwareId: Number(line.hardwareId), // Ensure it's a number
                name: line.name
              }));
            setExistingHardwareIds(hardwareIds);
          }
        } catch (error) {
          console.error('Error fetching existing hardware IDs:', error);
        }
      };
      
      const fetchAvailableSystems = async () => {
        try {
          const response = await fetch(`${apiUrl}/systems`);
          if (response.ok) {
            const data = await response.json();
            const systems = data.data || [];
            setAvailableSystems(systems.map((system: any) => ({
              id: system.id,
              systemName: system.systemName || system.systemname || 'Unknown System'
            })));
          }
        } catch (error) {
          console.error('Error fetching available systems:', error);
        }
      };
      
      fetchDefaults();
      fetchExistingHardwareIds();
      fetchAvailableSystems();
      // Reset to basic tab when opening
      setActiveTab('basic');
      // Reset initial load flag when modal opens
      setInitialLoadComplete(false);
    }
  }, [isOpen, apiUrl]);

  useEffect(() => {
    if (initialData) {
      console.log('LineFormModalV2: Setting form data from initialData:', {
        pocketCloseDelays: initialData.pocketCloseDelays,
        numberOfProductPockets: initialData.numberOfProductPockets,
        systemDefaults: systemDefaults.defaultPocketCloseDelays
      });
      
      const existingArmTiming = initialData.armTimingSettings ? 
        (typeof initialData.armTimingSettings === 'string' ? 
          JSON.parse(initialData.armTimingSettings) : 
          initialData.armTimingSettings) : null;
          
      setFormData({
        hardwareId: initialData.hardwareId || initialData.lineId || '',
        name: initialData.name,
        systemId: initialData.systemId || initialData.systemid || '',
        numberOfStations: initialData.numberOfStations,
        numberOfProductPockets: initialData.numberOfProductPockets,
        conveyorStartDelay: initialData.conveyorStartDelay || systemDefaults.conveyorStartDelay,
        conveyorStopDelay: initialData.conveyorStopDelay || systemDefaults.conveyorStopDelay,
        bufferHopperOpenDelay: initialData.bufferHopperOpenDelay || systemDefaults.bufferHopperOpenDelay,
        bufferHopperCloseDelay: initialData.bufferHopperCloseDelay || systemDefaults.bufferHopperCloseDelay,
        weighingDelay: initialData.weighingDelay || systemDefaults.weighingDelay,
        zeroingDelay: initialData.zeroingDelay || systemDefaults.zeroingDelay,
        zeroThresholdWeight: initialData.zeroThresholdWeight || systemDefaults.zeroThresholdWeight,
        hopperCapacity: initialData.hopperCapacity || systemDefaults.hopperCapacity,
        defaultInfeedTargetWeight: initialData.defaultInfeedTargetWeight || systemDefaults.defaultInfeedTargetWeight,
        armTimingSettings: existingArmTiming || systemDefaults.armTimingSettings || [],
        outfeedHopperCapacity: initialData.outfeedHopperCapacity || systemDefaults.outfeedHopperCapacity,
        outfeedCycleWaitTime: initialData.outfeedCycleWaitTime || systemDefaults.outfeedCycleWaitTime,
        outfeedWeighDelay: initialData.outfeedWeighDelay || systemDefaults.outfeedWeighDelay,
        outfeedZeroDelay: initialData.outfeedZeroDelay || systemDefaults.outfeedZeroDelay,
        outfeedZeroThresholdWeight: initialData.outfeedZeroThresholdWeight || systemDefaults.outfeedZeroThresholdWeight,
        outfeedBreakoutAttempts: initialData.outfeedBreakoutAttempts || systemDefaults.outfeedBreakoutAttempts,
        outfeedPocketBufferHopperCloseDelay: initialData.outfeedPocketBufferHopperCloseDelay || 
          systemDefaults.outfeedPocketBufferHopperCloseDelay,
        pocketCloseDelays: initialData.pocketCloseDelays && initialData.pocketCloseDelays.length > 0
          ? (console.log('Using existing pocket delays:', initialData.pocketCloseDelays), initialData.pocketCloseDelays)
          : (console.log('Creating default pocket delays for', initialData.numberOfProductPockets, 'pockets'), 
             Array(initialData.numberOfProductPockets || 0).fill(0).map((_, i) => 
              systemDefaults.defaultPocketCloseDelays[i] || 1500)),
      });
      // Mark initial load as complete after setting initial data
      setTimeout(() => setInitialLoadComplete(true), 100);
    } else {
      setFormData({
        hardwareId: '', 
        name: '', 
        systemId: '',
        numberOfStations: systemDefaults.defaultNumberOfStations, 
        numberOfProductPockets: systemDefaults.defaultNumberOfPockets,
        conveyorStartDelay: systemDefaults.conveyorStartDelay, 
        conveyorStopDelay: systemDefaults.conveyorStopDelay, 
        bufferHopperOpenDelay: systemDefaults.bufferHopperOpenDelay,
        bufferHopperCloseDelay: systemDefaults.bufferHopperCloseDelay, 
        weighingDelay: systemDefaults.weighingDelay, 
        zeroingDelay: systemDefaults.zeroingDelay, 
        zeroThresholdWeight: systemDefaults.zeroThresholdWeight, 
        hopperCapacity: systemDefaults.hopperCapacity, 
        defaultInfeedTargetWeight: systemDefaults.defaultInfeedTargetWeight,
        armTimingSettings: systemDefaults.armTimingSettings || [],
        outfeedHopperCapacity: systemDefaults.outfeedHopperCapacity,
        outfeedCycleWaitTime: systemDefaults.outfeedCycleWaitTime,
        outfeedWeighDelay: systemDefaults.outfeedWeighDelay,
        outfeedZeroDelay: systemDefaults.outfeedZeroDelay,
        outfeedZeroThresholdWeight: systemDefaults.outfeedZeroThresholdWeight,
        outfeedBreakoutAttempts: systemDefaults.outfeedBreakoutAttempts,
        outfeedPocketBufferHopperCloseDelay: systemDefaults.outfeedPocketBufferHopperCloseDelay,
        pocketCloseDelays: Array(systemDefaults.defaultNumberOfPockets).fill(0).map((_, i) => 
          systemDefaults.defaultPocketCloseDelays[i] || 1500)
      });
      // Mark initial load as complete for new lines too
      setTimeout(() => setInitialLoadComplete(true), 100);
    }
    setErrors({});
  }, [isOpen, initialData, systemDefaults]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let parsedValue: string | number = value;

    if (name === 'hardwareId' || name === 'numberOfStations' || name === 'numberOfProductPockets' ||
        name === 'conveyorStartDelay' || name === 'conveyorStopDelay' || name === 'bufferHopperOpenDelay' ||
        name === 'bufferHopperCloseDelay' || name === 'weighingDelay' || name === 'zeroingDelay' ||
        name === 'outfeedCycleWaitTime' || name === 'outfeedWeighDelay' || name === 'outfeedZeroDelay' ||
        name === 'outfeedBreakoutAttempts' || name === 'outfeedPocketBufferHopperCloseDelay') {
      parsedValue = value === '' ? '' : parseInt(value, 10);
    } else if (name === 'zeroThresholdWeight' || name === 'hopperCapacity' || name === 'defaultInfeedTargetWeight' ||
               name === 'outfeedHopperCapacity' || name === 'outfeedZeroThresholdWeight') {
      parsedValue = value === '' ? '' : parseFloat(value);
    }
    
    // Real-time hardware ID validation
    if (name === 'hardwareId' && parsedValue !== '') {
      const hwId = Number(parsedValue);
      const duplicate = existingHardwareIds.find(
        item => Number(item.hardwareId) === hwId && item.id !== initialData?.id
      );
      if (duplicate) {
        setErrors(prev => ({ 
          ...prev, 
          [name]: `Hardware ID ${hwId} is already in use by line: ${duplicate.name}` 
        }));
      } else {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    } else {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    setFormData(prev => ({ ...prev, [name]: parsedValue }));
  };

  const handleInfeedChange = (field: keyof LineFormData, value: number | '' | ArmTimingData[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleOutfeedChange = (field: string, value: number | '') => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handlePocketDelayChange = (index: number, value: number | '') => {
    setFormData(prev => {
      const newPocketDelays = [...prev.pocketCloseDelays];
      newPocketDelays[index] = value;
      return { ...prev, pocketCloseDelays: newPocketDelays };
    });
    setErrors(prev => ({ ...prev, [`pocketCloseDelay_${index}`]: '' }));
  };

  // Track if the initial data has been loaded
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Update pocket delays when number of pockets changes (but not on initial load)
  useEffect(() => {
    if (!initialLoadComplete) return; // Skip this effect until initial data is loaded
    
    const numPockets = Number(formData.numberOfProductPockets) || 0;
    if (numPockets > 0) {
      // Ensure we have the right number of delays when user changes pocket count
      const newDelays = Array(numPockets).fill(0).map((_, i) => 
        formData.pocketCloseDelays[i] !== undefined 
          ? formData.pocketCloseDelays[i] 
          : (systemDefaults.defaultPocketCloseDelays[i] || 1500)
      );
      if (newDelays.length !== formData.pocketCloseDelays.length) {
        console.log('Updating pocket delays due to pocket count change:', newDelays);
        setFormData(prev => ({ ...prev, pocketCloseDelays: newDelays }));
      }
    } else {
      // Clear delays if no pockets
      if (formData.pocketCloseDelays.length > 0) {
        setFormData(prev => ({ ...prev, pocketCloseDelays: [] }));
      }
    }
  }, [formData.numberOfProductPockets, formData.pocketCloseDelays.length, initialLoadComplete, systemDefaults.defaultPocketCloseDelays]);

  const validate = (): boolean => {
    let isValid = true;
    const newErrors: Record<string, string> = {};

    // Basic validation
    if (formData.hardwareId !== '') {
      const hwId = Number(formData.hardwareId);
      if (isNaN(hwId) || hwId < 1 || hwId > 15) {
        newErrors.hardwareId = 'Hardware ID must be a number between 1 and 15.';
        isValid = false;
      } else {
        // Check for duplicate hardware ID
        const duplicate = existingHardwareIds.find(
          item => Number(item.hardwareId) === hwId && item.id !== initialData?.id
        );
        if (duplicate) {
          newErrors.hardwareId = `Hardware ID ${hwId} is already in use by line: ${duplicate.name}`;
          isValid = false;
        }
      }
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Line Name is required.';
      isValid = false;
    }
    
    // System selection is optional, so no validation needed
    
    if (formData.numberOfStations === '' || isNaN(Number(formData.numberOfStations)) || 
        Number(formData.numberOfStations) <= 0) {
      newErrors.numberOfStations = 'Number of Stations must be a positive number.';
      isValid = false;
    }
    
    if (formData.numberOfProductPockets === '' || isNaN(Number(formData.numberOfProductPockets)) || 
        Number(formData.numberOfProductPockets) < 1 || Number(formData.numberOfProductPockets) > 4) {
      newErrors.numberOfProductPockets = 'Number of Product Pockets must be between 1 and 4.';
      isValid = false;
    }

    // Validate infeed settings
    const infeedFields = ['conveyorStartDelay', 'conveyorStopDelay', 'bufferHopperOpenDelay', 
      'bufferHopperCloseDelay', 'weighingDelay', 'zeroingDelay'] as const;
    infeedFields.forEach(field => {
      const value = formData[field];
      if (value === '' || isNaN(Number(value)) || Number(value) < 0) {
        newErrors[field] = 'Must be a positive number.';
        isValid = false;
      }
    });

    if (formData.zeroThresholdWeight === '' || isNaN(Number(formData.zeroThresholdWeight)) || 
        Number(formData.zeroThresholdWeight) < 0) {
      newErrors.zeroThresholdWeight = 'Must be a positive number.';
      isValid = false;
    }

    if (formData.hopperCapacity === '' || isNaN(Number(formData.hopperCapacity)) || 
        Number(formData.hopperCapacity) <= 0) {
      newErrors.hopperCapacity = 'Must be a positive number.';
      isValid = false;
    }

    if (formData.defaultInfeedTargetWeight === '' || isNaN(Number(formData.defaultInfeedTargetWeight)) || 
        Number(formData.defaultInfeedTargetWeight) <= 0) {
      newErrors.defaultInfeedTargetWeight = 'Must be a positive number.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      // Show the tab with errors
      const hasBasicErrors = errors.hardwareId || errors.name || errors.numberOfStations || 
                            errors.numberOfProductPockets;
      const hasInfeedErrors = errors.conveyorStartDelay || errors.conveyorStopDelay || 
                              errors.bufferHopperOpenDelay || errors.bufferHopperCloseDelay || 
                              errors.weighingDelay || errors.zeroingDelay || 
                              errors.zeroThresholdWeight || errors.hopperCapacity || 
                              errors.defaultInfeedTargetWeight;
      
      if (hasBasicErrors) {
        setActiveTab('basic');
      } else if (hasInfeedErrors) {
        setActiveTab('infeed');
      }
      return;
    }
    
    const finalFormData = {
      hardwareId: formData.hardwareId === '' ? undefined : Number(formData.hardwareId),
      name: formData.name,
      systemId: formData.systemId || undefined,
      numberOfStations: Number(formData.numberOfStations),
      numberOfProductPockets: Number(formData.numberOfProductPockets),
      productAssignments: [],
      conveyorStartDelay: Number(formData.conveyorStartDelay),
      conveyorStopDelay: Number(formData.conveyorStopDelay),
      bufferHopperOpenDelay: Number(formData.bufferHopperOpenDelay),
      bufferHopperCloseDelay: Number(formData.bufferHopperCloseDelay),
      weighingDelay: Number(formData.weighingDelay),
      zeroingDelay: Number(formData.zeroingDelay),
      zeroThresholdWeight: Number(formData.zeroThresholdWeight),
      hopperCapacity: Number(formData.hopperCapacity),
      defaultInfeedTargetWeight: Number(formData.defaultInfeedTargetWeight),
      armTimingSettings: JSON.stringify(formData.armTimingSettings),
      outfeedHopperCapacity: Number(formData.outfeedHopperCapacity),
      outfeedCycleWaitTime: Number(formData.outfeedCycleWaitTime),
      outfeedWeighDelay: Number(formData.outfeedWeighDelay),
      outfeedZeroDelay: Number(formData.outfeedZeroDelay),
      outfeedZeroThresholdWeight: Number(formData.outfeedZeroThresholdWeight),
      outfeedBreakoutAttempts: Number(formData.outfeedBreakoutAttempts),
      outfeedPocketBufferHopperCloseDelay: Number(formData.outfeedPocketBufferHopperCloseDelay),
      pocketCloseDelays: formData.pocketCloseDelays.map(d => Number(d) || 1500),
    };

    if (initialData) {
      onSubmit({ ...initialData, ...finalFormData }, false);
    } else {
      onSubmit(finalFormData, true);
    }
  };

  const tabBaseStyle = "py-2 px-4 text-sm font-medium text-center cursor-pointer focus:outline-none transition-colors";
  const activeTabStyle = "border-b-2 border-indigo-600 text-indigo-600";
  const inactiveTabStyle = "border-b-2 border-transparent text-slate-500 hover:text-indigo-600 hover:border-indigo-300";

  const hasBasicErrors = !!(errors.hardwareId || errors.name || errors.numberOfStations || 
                           errors.numberOfProductPockets);
  const hasInfeedErrors = !!(errors.conveyorStartDelay || errors.conveyorStopDelay || 
                            errors.bufferHopperOpenDelay || errors.bufferHopperCloseDelay || 
                            errors.weighingDelay || errors.zeroingDelay || 
                            errors.zeroThresholdWeight || errors.hopperCapacity || 
                            errors.defaultInfeedTargetWeight);
  const hasOutfeedErrors = !!(errors.outfeedHopperCapacity || errors.outfeedCycleWaitTime || 
                             errors.outfeedWeighDelay || errors.outfeedZeroDelay || 
                             errors.outfeedZeroThresholdWeight || errors.outfeedBreakoutAttempts || 
                             errors.outfeedPocketBufferHopperCloseDelay);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="xl">
      <form onSubmit={handleSubmit} noValidate className="flex flex-col h-full">
        {/* Tab Navigation */}
        <div className="border-b border-slate-200 mb-6">
          <nav className="-mb-px flex space-x-4" aria-label="Line Settings Tabs">
            <button
              type="button"
              onClick={() => setActiveTab('basic')}
              className={`${tabBaseStyle} ${activeTab === 'basic' ? activeTabStyle : inactiveTabStyle}`}
              aria-current={activeTab === 'basic' ? 'page' : undefined}
            >
              Basic Settings
              {hasBasicErrors && <span className="ml-1 text-red-500">•</span>}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('infeed')}
              className={`${tabBaseStyle} ${activeTab === 'infeed' ? activeTabStyle : inactiveTabStyle}`}
              aria-current={activeTab === 'infeed' ? 'page' : undefined}
            >
              Infeed Settings
              {hasInfeedErrors && <span className="ml-1 text-red-500">•</span>}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('outfeed')}
              className={`${tabBaseStyle} ${activeTab === 'outfeed' ? activeTabStyle : inactiveTabStyle}`}
              aria-current={activeTab === 'outfeed' ? 'page' : undefined}
            >
              Outfeed Settings
              {hasOutfeedErrors && <span className="ml-1 text-red-500">•</span>}
            </button>
          </nav>
        </div>

        {/* Tab Content - Fixed height for consistency */}
        <div className="h-[500px] overflow-y-auto px-1">
          {activeTab === 'basic' && (
            <div className="space-y-6 py-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Basic Line Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <InputField 
                    label="Hardware ID" 
                    name="hardwareId" 
                    type="number" 
                    value={formData.hardwareId} 
                    onChange={handleChange} 
                    error={errors.hardwareId} 
                    min="1" 
                    max="15" 
                  />
                  <p className="mt-1 text-xs text-red-600 font-medium">
                    Important: Must match Hardware ID Node Settings
                  </p>
                </div>
                <InputField 
                  label="Line Name" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  error={errors.name} 
                  required 
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  label="System Assignment"
                  name="systemId"
                  type="select"
                  value={formData.systemId}
                  onChange={handleChange}
                  error={errors.systemId}
                >
                  <option value="">No System (Optional)</option>
                  {availableSystems.map(system => (
                    <option key={system.id} value={system.id}>
                      {system.systemName}
                    </option>
                  ))}
                </InputField>
                <div className="flex items-end">
                  <p className="text-xs text-slate-500 pb-2">
                    Lines can optionally be assigned to systems for organizational purposes.
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField 
                  label="Number of Stations" 
                  name="numberOfStations" 
                  type="number" 
                  value={formData.numberOfStations} 
                  onChange={handleChange} 
                  error={errors.numberOfStations} 
                  required 
                  min="1"
                  disabled={systemDefaults.lockNumberOfStations}
                  lockedMessage={systemDefaults.lockNumberOfStations ? 
                    "This value is locked by system defaults and cannot be changed" : undefined}
                />
                <InputField
                  label="Number of Product Pockets"
                  name="numberOfProductPockets"
                  type="select"
                  value={formData.numberOfProductPockets}
                  onChange={handleChange}
                  error={errors.numberOfProductPockets}
                  required
                  disabled={systemDefaults.lockNumberOfPockets}
                  lockedMessage={systemDefaults.lockNumberOfPockets ? 
                    "This value is locked by system defaults and cannot be changed" : undefined}
                >
                  <option value="">Select pockets...</option>
                  <option value="1">1 Pocket</option>
                  <option value="2">2 Pockets</option>
                  <option value="3">3 Pockets</option>
                  <option value="4">4 Pockets</option>
                </InputField>
              </div>
            </div>
          )}

          {activeTab === 'infeed' && (
            <div className="py-4">
              <InfeedSettingsV2 
              infeedData={{
                conveyorStartDelay: formData.conveyorStartDelay,
                conveyorStopDelay: formData.conveyorStopDelay,
                bufferHopperOpenDelay: formData.bufferHopperOpenDelay,
                bufferHopperCloseDelay: formData.bufferHopperCloseDelay,
                weighingDelay: formData.weighingDelay,
                zeroingDelay: formData.zeroingDelay,
                zeroThresholdWeight: formData.zeroThresholdWeight,
                hopperCapacity: formData.hopperCapacity,
                defaultInfeedTargetWeight: formData.defaultInfeedTargetWeight,
                armTimingSettings: formData.armTimingSettings,
              }}
              onChange={handleInfeedChange}
              errors={errors}
              numberOfStations={Number(formData.numberOfStations) || 10}
              systemDefaults={{
                conveyorStartDelay: systemDefaults.conveyorStartDelay,
                conveyorStopDelay: systemDefaults.conveyorStopDelay,
                bufferHopperOpenDelay: systemDefaults.bufferHopperOpenDelay,
                bufferHopperCloseDelay: systemDefaults.bufferHopperCloseDelay,
                weighingDelay: systemDefaults.weighingDelay,
                zeroingDelay: systemDefaults.zeroingDelay,
                zeroThresholdWeight: systemDefaults.zeroThresholdWeight,
                hopperCapacity: systemDefaults.hopperCapacity,
                defaultInfeedTargetWeight: systemDefaults.defaultInfeedTargetWeight,
                armTimingSettings: systemDefaults.armTimingSettings,
              }}
              showDefaultIndicators={!!initialData}
            />
            </div>
          )}

          {activeTab === 'outfeed' && (
            <div className="space-y-4 py-4">
              {/* Outfeed Sub-tabs */}
              <div className="border-b border-slate-200">
                <nav className="-mb-px flex space-x-4" aria-label="Outfeed Settings Sub-tabs">
                  <button
                    type="button"
                    onClick={() => setOutfeedSubTab('system')}
                    className={`py-2 px-3 text-sm font-medium text-center cursor-pointer focus:outline-none transition-colors ${
                      outfeedSubTab === 'system' 
                        ? 'border-b-2 border-indigo-500 text-indigo-600' 
                        : 'border-b-2 border-transparent text-slate-500 hover:text-indigo-600 hover:border-indigo-300'
                    }`}
                  >
                    Station Weigh Hopper
                  </button>
                  <button
                    type="button"
                    onClick={() => setOutfeedSubTab('pockets')}
                    className={`py-2 px-3 text-sm font-medium text-center cursor-pointer focus:outline-none transition-colors ${
                      outfeedSubTab === 'pockets' 
                        ? 'border-b-2 border-indigo-500 text-indigo-600' 
                        : 'border-b-2 border-transparent text-slate-500 hover:text-indigo-600 hover:border-indigo-300'
                    }`}
                    disabled={!formData.numberOfProductPockets}
                  >
                    Pocket Delays
                    {!formData.numberOfProductPockets && <span className="ml-1 text-xs">(Select pockets first)</span>}
                  </button>
                </nav>
              </div>

              {/* Sub-tab Content */}
              {outfeedSubTab === 'system' ? (
                <div className="pt-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4">Station Weigh Hopper Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      label="Hopper Capacity"
                      name="outfeedHopperCapacity"
                      type="number"
                      value={formData.outfeedHopperCapacity}
                      onChange={handleChange}
                      error={errors.outfeedHopperCapacity}
                      min="0"
                      step="0.1"
                      unit={weightUnit === 'KGS' ? 'kg' : 'lbs'}
                      defaultValue={systemDefaults.outfeedHopperCapacity}
                      showDefaultIndicator={!!initialData}
                    />
                    <InputField
                      label="Cycle Wait Time"
                      name="outfeedCycleWaitTime"
                      type="number"
                      value={formData.outfeedCycleWaitTime}
                      onChange={handleChange}
                      error={errors.outfeedCycleWaitTime}
                      min="0"
                      unit="ms"
                      defaultValue={systemDefaults.outfeedCycleWaitTime}
                      showDefaultIndicator={!!initialData}
                    />
                    <InputField
                      label="Weigh Delay"
                      name="outfeedWeighDelay"
                      type="number"
                      value={formData.outfeedWeighDelay}
                      onChange={handleChange}
                      error={errors.outfeedWeighDelay}
                      min="0"
                      unit="ms"
                      defaultValue={systemDefaults.outfeedWeighDelay}
                      showDefaultIndicator={!!initialData}
                    />
                    <InputField
                      label="Zero Delay"
                      name="outfeedZeroDelay"
                      type="number"
                      value={formData.outfeedZeroDelay}
                      onChange={handleChange}
                      error={errors.outfeedZeroDelay}
                      min="0"
                      unit="ms"
                      defaultValue={systemDefaults.outfeedZeroDelay}
                      showDefaultIndicator={!!initialData}
                    />
                    <InputField
                      label="Zero Threshold Weight"
                      name="outfeedZeroThresholdWeight"
                      type="number"
                      value={formData.outfeedZeroThresholdWeight}
                      onChange={handleChange}
                      error={errors.outfeedZeroThresholdWeight}
                      min="0"
                      step="0.01"
                      unit={weightUnit === 'KGS' ? 'kg' : 'lbs'}
                      defaultValue={systemDefaults.outfeedZeroThresholdWeight}
                      showDefaultIndicator={!!initialData}
                    />
                    <InputField
                      label="Breakout Attempts"
                      name="outfeedBreakoutAttempts"
                      type="number"
                      value={formData.outfeedBreakoutAttempts}
                      onChange={handleChange}
                      error={errors.outfeedBreakoutAttempts}
                      min="0"
                      defaultValue={systemDefaults.outfeedBreakoutAttempts}
                      showDefaultIndicator={!!initialData}
                    />
                    <InputField
                      label="Weigh Hopper Close Delay"
                      name="outfeedPocketBufferHopperCloseDelay"
                      type="number"
                      value={formData.outfeedPocketBufferHopperCloseDelay}
                      onChange={handleChange}
                      error={errors.outfeedPocketBufferHopperCloseDelay}
                      min="0"
                      unit="ms"
                      defaultValue={systemDefaults.outfeedPocketBufferHopperCloseDelay}
                      showDefaultIndicator={!!initialData}
                    />
                  </div>
                </div>
              ) : (
                <div className="pt-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4">Pocket-Specific Close Delays</h3>
                  {formData.numberOfProductPockets ? (
                    <div className="space-y-4">
                      <p className="text-sm text-slate-600">
                        Configure the close delay for each product pocket. These delays control how long each pocket waits before closing.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        {Array.from({ length: Number(formData.numberOfProductPockets) }, (_, index) => {
                          const defaultValue = systemDefaults.defaultPocketCloseDelays?.[index] || 1500;
                          const currentValue = formData.pocketCloseDelays[index];
                          const isDifferent = !!initialData && currentValue !== undefined && currentValue !== '' && currentValue !== defaultValue;
                          
                          return (
                            <div key={index} className="p-3 bg-slate-50 rounded-lg">
                              <label className="block text-sm font-medium text-slate-700 mb-1">
                                Pocket {index + 1} Close Delay
                                {isDifferent && (
                                  <span className="ml-2 relative group">
                                    <span className="text-blue-600 font-bold cursor-help">•</span>
                                    <div className="absolute z-50 invisible group-hover:visible bg-slate-800 text-white text-xs rounded-md py-1 px-2 bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap shadow-lg">
                                      <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-800"></div>
                                      Default: {defaultValue}ms
                                    </div>
                                  </span>
                                )}
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  name={`pocketDelay_${index}`}
                                  value={formData.pocketCloseDelays[index] !== undefined ? formData.pocketCloseDelays[index] : ''}
                                  onChange={(e) => handlePocketDelayChange(index, e.target.value === '' ? '' : Number(e.target.value))}
                                  min="0"
                                  className={`w-full px-3 py-2 pr-12 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                                    errors[`pocketCloseDelay_${index}`] ? 'border-red-500' : 'border-slate-300'
                                  }`}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
                                  ms
                                </span>
                              </div>
                              {errors[`pocketCloseDelay_${index}`] && (
                                <p className="mt-1 text-xs text-red-600">{errors[`pocketCloseDelay_${index}`]}</p>
                              )}
                              <p className="text-xs text-slate-500 mt-1">
                                Default: {defaultValue}ms
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <p>Please select the number of product pockets in the Basic Settings tab first.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Form Actions - Fixed at bottom */}
        <div className="mt-auto pt-4 border-t border-slate-200 flex justify-end space-x-3 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            {initialData ? 'Update' : 'Create'} Line
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default LineFormModalV2;